import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { LoadScript } from "@react-google-maps/api";
import {
  AutoComplete,
  Breadcrumb,
  Button,
  Checkbox,
  Col,
  Collapse,
  DatePicker,
  Dropdown,
  Form,
  Input,
  Menu,
  Popconfirm,
  Row,
  Space,
  Spin,
  Table,
  Tooltip,
  Pagination,
  Select,
} from "antd";
import "antd/dist/antd.min.css";
import { debounce } from "lodash";
import { getRequest, patchRequest, postRequest } from "../../axios";
import ButtonField from "../../common/htmlComponents/ButtonField";
import { ReactComponent as Archive } from "../../image/Archive.svg";
import { ReactComponent as Archiveicon } from "../../image/Archiveicon.svg";
import { ReactComponent as Editicon } from "../../image/Editicon.svg";
import scan_unchecked_red from "../../image/scan_unchecked_red.png";
import scan_check_green from "../../image/scan_check_green.png";
import SearchIcon from "../../image/SearchIcon.svg";
import wrongicon from "../../image/wrongicon.png";
import dayjs from "dayjs";
import { ReactComponent as ArchiveButtonicon } from "../../image/ArchiveButtonicon.svg";

import "../../styles/Main.scss";
import {
  ErrorNotificationMsg,
  SuccessNotificationMsg,
} from "../../utils/Notifications";

import {
  FilterOutlined,
  InsertRowLeftOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { authUserData } from "../../utils/Helper";
import SelectRouteModal from "./SelectRouteModal";
import ResizeObserver from "rc-resize-observer";
import PropTypes from "prop-types";
import { VariableSizeGrid as Grid } from "react-window";
import { useRouteNameChanges } from "../RouteNameProvider";
const { Option } = Select;
const { Panel } = Collapse;
const libraries = ["places"];
// Custom vertical scrollbar overlay component
const VerticalScrollbar = ({ height, contentHeight, scrollTop, onScroll }) => {
  const thumbHeight = Math.max((height / contentHeight) * height, 48);
  const maxScrollTop = contentHeight - height;
  const thumbTop = (scrollTop / maxScrollTop) * (height - thumbHeight) || 0;

  const thumbRef = useRef();
  const dragging = useRef(false);
  const dragStartY = useRef(0);
  const dragStartScroll = useRef(0);

  const handlePointerMove = (e) => {
    if (!dragging.current) return;
    const deltaY = e.clientY - dragStartY.current;
    const newScrollTop = Math.min(
      Math.max(
        dragStartScroll.current +
          (deltaY / (height - thumbHeight)) * maxScrollTop,
        0
      ),
      maxScrollTop
    );
    onScroll(newScrollTop);
  };

  const handlePointerUp = (e) => {
    dragging.current = false;
    document.body.style.userSelect = "";
    if (thumbRef.current) {
      thumbRef.current.releasePointerCapture(e.pointerId);
      thumbRef.current.removeEventListener("pointermove", handlePointerMove);
      thumbRef.current.removeEventListener("pointerup", handlePointerUp);
    }
  };

  const handleThumbPointerDown = (e) => {
    dragging.current = true;
    dragStartY.current = e.clientY;
    dragStartScroll.current = scrollTop;
    document.body.style.userSelect = "none";
    if (thumbRef.current) {
      thumbRef.current.setPointerCapture(e.pointerId);
      thumbRef.current.addEventListener("pointermove", handlePointerMove);
      thumbRef.current.addEventListener("pointerup", handlePointerUp);
    }
  };

  if (contentHeight <= height) return null;

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        right: 0,
        width: 10,
        height,
        background: "rgba(0,0,0,0.02)",
        zIndex: 30,
        borderRadius: 7,
        pointerEvents: "auto",
      }}
    >
      <div
        ref={thumbRef}
        style={{
          position: "absolute",
          left: 2,
          width: 6,
          height: thumbHeight,
          top: thumbTop,
          background: "#F95C19", // orange theme color
          borderRadius: 5,
          cursor: "pointer",
        }}
        onPointerDown={handleThumbPointerDown}
      />
    </div>
  );
};
VerticalScrollbar.propTypes = {
  height: PropTypes.number.isRequired,
  contentHeight: PropTypes.number.isRequired,
  scrollTop: PropTypes.number.isRequired,
  onScroll: PropTypes.func.isRequired,
};

// Custom horizontal scrollbar overlay component
const HorizontalScrollbar = ({
  width,
  contentWidth,
  scrollLeft,
  onScroll,
  height = 16,
}) => {
  const thumbWidth = Math.max((width / contentWidth) * width, 48);
  const maxScrollLeft = contentWidth - width;
  const thumbLeft = (scrollLeft / maxScrollLeft) * (width - thumbWidth) || 0;

  const thumbRef = useRef();
  const dragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartScroll = useRef(0);

  const handlePointerMove = (e) => {
    if (!dragging.current) return;
    const deltaX = e.clientX - dragStartX.current;
    const newScrollLeft = Math.min(
      Math.max(
        dragStartScroll.current +
          (deltaX / (width - thumbWidth)) * maxScrollLeft,
        0
      ),
      maxScrollLeft
    );
    onScroll(newScrollLeft);
  };

  const handlePointerUp = (e) => {
    dragging.current = false;
    document.body.style.userSelect = "";
    if (thumbRef.current) {
      thumbRef.current.releasePointerCapture(e.pointerId);
      thumbRef.current.removeEventListener("pointermove", handlePointerMove);
      thumbRef.current.removeEventListener("pointerup", handlePointerUp);
    }
  };

  const handleThumbPointerDown = (e) => {
    dragging.current = true;
    dragStartX.current = e.clientX;
    dragStartScroll.current = scrollLeft;
    document.body.style.userSelect = "none";
    if (thumbRef.current) {
      thumbRef.current.setPointerCapture(e.pointerId);
      thumbRef.current.addEventListener("pointermove", handlePointerMove);
      thumbRef.current.addEventListener("pointerup", handlePointerUp);
    }
  };

  if (contentWidth <= width) return null;

  return (
    <div
      style={{
        position: "absolute",
        left: 0,
        bottom: 0,
        height: height,
        width,
        background: "rgba(0,0,0,0.02)",
        zIndex: 30,
        borderRadius: 7,
        pointerEvents: "auto",
      }}
    >
      <div
        ref={thumbRef}
        style={{
          position: "absolute",
          top: 2,
          height: height - 4,
          width: thumbWidth,
          left: thumbLeft,
          background: "#F95C19", // orange theme color
          borderRadius: 5,
          cursor: "pointer",
        }}
        onPointerDown={handleThumbPointerDown}
      />
    </div>
  );
};
HorizontalScrollbar.propTypes = {
  width: PropTypes.number.isRequired,
  contentWidth: PropTypes.number.isRequired,
  scrollLeft: PropTypes.number.isRequired,
  onScroll: PropTypes.func.isRequired,
  height: PropTypes.number,
};
const VirtualizedTable = ({
  columns,
  data,
  selectedRow,
  setSelectedRow,
  rowKey,
  height = 600,
  width = 1200, // will be overridden by totalTableWidth
  rowHeight = 54,
  headerHeight = 54,
  onRowClick,
  customCellRender,
  loading = false,
  calculateTotalPrice, // <-- add this
  sortState, // <-- add this
  setSortState, // <-- add this
  gridRef,
  setScrollPosition,
  scrollPosition,
}) => {
  const [containerWidth, setContainerWidth] = useState(width);
  const headerRef = useRef();

  // Reset grid layout when columns change to fix alignment
  useEffect(() => {
    if (gridRef.current && gridRef.current.resetAfterColumnIndex) {
      gridRef.current.resetAfterColumnIndex(0, true);
    }
  }, [columns]);

  // Move these up so showScrollbarSpacer is available before columnsWithSelection
  const rowCount = data.length;
  const contentHeight = rowCount * rowHeight;
  const SCROLLBAR_WIDTH = 10;
  const showScrollbarSpacer = contentHeight > height - headerHeight;

  // Memoized, non-mutating columns with select-all logic
  const columnsWithSelection = useMemo(() => {
    if (!columns || columns.length === 0) return [];
    // Ensure all columns have a width
    let cols = columns.map((col) => ({ ...col, width: col.width || 120 }));
    if (cols[0].key === "selection") {
      // Select all logic for the header checkbox
      cols[0] = {
        ...cols[0],
        title: () => {
          const allVisibleSelected =
            data.length > 0 &&
            data.every((record) =>
              selectedRow.includes(
                typeof rowKey === "function" ? rowKey(record) : record[rowKey]
              )
            );
          const someVisibleSelected = data.some((record) =>
            selectedRow.includes(
              typeof rowKey === "function" ? rowKey(record) : record[rowKey]
            )
          );
          return (
            <input
              style={{
                width: "16px",
                height: "16px",
                verticalAlign: "middle",
                accentColor: "#fff",
              }}
              type="checkbox"
              checked={allVisibleSelected}
              ref={(el) => {
                if (el)
                  el.indeterminate = !allVisibleSelected && someVisibleSelected;
              }}
              onChange={(e) => {
                if (e.target.checked) {
                  const newKeys = [
                    ...selectedRow,
                    ...data
                      .map((record) =>
                        typeof rowKey === "function"
                          ? rowKey(record)
                          : record[rowKey]
                      )
                      .filter((key) => !selectedRow.includes(key)),
                  ];
                  setSelectedRow(newKeys);
                  calculateTotalPrice(newKeys);
                } else {
                  const visibleKeys = data.map((record) =>
                    typeof rowKey === "function"
                      ? rowKey(record)
                      : record[rowKey]
                  );
                  setSelectedRow(
                    selectedRow.filter((key) => !visibleKeys.includes(key))
                  );
                }
              }}
            />
          );
        },
      };
    }
    // If a scrollbar spacer is present, increase the last column's width slightly
    if (cols.length > 0 && showScrollbarSpacer) {
      cols[cols.length - 1] = {
        ...cols[cols.length - 1],
        width: cols[cols.length - 1].width || 120, // Do not add extra width, use spacer column instead
      };
      // Add a spacer column for the scrollbar
      cols = [
        ...cols,
        {
          key: "scrollbar-spacer",
          width: SCROLLBAR_WIDTH,
          isSpacer: true,
        },
      ];
    }
    return cols;
  }, [
    columns,
    selectedRow,
    setSelectedRow,
    data,
    rowKey,
    calculateTotalPrice,
    showScrollbarSpacer,
  ]);

  const columnCount = columnsWithSelection.length;

  // Adjust getColumnWidth to handle the spacer column
  const getColumnWidth = (index) => columnsWithSelection[index]?.width || 120;
  const getRowHeight = () => rowHeight;

  // Calculate total table width as the sum of all column widths
  const totalTableWidth = columnsWithSelection.reduce(
    (sum, col) => sum + (col.width || 120),
    0
  );

  // Sticky header row
  const headerColumns = columnsWithSelection;
  const renderHeader = () =>
    columnsWithSelection.length === 0 ? null : (
      <div
        style={{
          width: containerWidth,
          overflowX: "hidden",
          overflowY: "visible",
          background: "transparent",
          position: "sticky",
          top: 0,
          zIndex: 2,
        }}
      >
        <div
          ref={headerRef}
          className="virtual-table-header-row"
          style={{
            display: "flex",
            width:
              totalTableWidth + (showScrollbarSpacer ? SCROLLBAR_WIDTH : 0),
            height: 44,
            background: "#F95C19", // Orange header
            borderBottom: "1px solid #e8e8e8",
            alignItems: "center",
            color: "#fff",
            fontWeight: 600,
            borderRadius: "8px 8px 0 0",
            overflow: "hidden",
            willChange: "transform",
            transform: `translateX(-${scrollPosition.left}px)`,
            transition: "none",
          }}
        >
          {headerColumns.map((col, i) =>
            col.isSpacer ? (
              <div
                key="scrollbar-spacer"
                style={{
                  width: SCROLLBAR_WIDTH,
                  minWidth: SCROLLBAR_WIDTH,
                  maxWidth: SCROLLBAR_WIDTH,
                  background: "transparent",
                  border: "none",
                }}
              />
            ) : (
              <div
                key={col.key}
                style={{
                  width: getColumnWidth(i),
                  minWidth: getColumnWidth(i),
                  maxWidth: getColumnWidth(i),
                  fontWeight: 500,
                  padding: "0 10px",
                  display: "flex",
                  alignItems: "center",
                  borderRight:
                    i === headerColumns.length - 1
                      ? "none"
                      : "1px solid #f0f0f0",
                  boxSizing: "border-box",
                  userSelect: "none",
                  cursor: col.sorter ? "pointer" : "default",
                  justifyContent: "flex-start",
                  height: 44,
                  background: "#F95C19",
                  color: "#fff",
                  borderTopLeftRadius: i === 0 ? 8 : 0,
                  borderTopRightRadius: i === headerColumns.length - 1 ? 8 : 0,
                  fontSize: 12,
                  letterSpacing: 0.2,
                  verticalAlign: "middle",
                }}
                onClick={() => {
                  if (!col.sorter) return;
                  let nextOrder = "ascend";
                  if (sortState.key === col.key && sortState.order === "ascend")
                    nextOrder = "descend";
                  else if (
                    sortState.key === col.key &&
                    sortState.order === "descend"
                  )
                    nextOrder = null;
                  setSortState({ key: col.key, order: nextOrder });
                }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  {typeof col.title === "function" ? col.title() : col.title}
                  {col.sorter && (
                    <span
                      style={{
                        marginLeft: 5,
                        display: "flex",
                        flexDirection: "column",
                        fontSize: 9,
                        justifyContent: "center",
                        alignItems: "center",
                        height: 22,
                        gap: 3,
                      }}
                    >
                      <span
                        style={{
                          color:
                            sortState.key === col.key &&
                            sortState.order === "ascend"
                              ? "#fff"
                              : "#ffd6c2",
                          fontWeight:
                            sortState.key === col.key &&
                            sortState.order === "ascend"
                              ? 700
                              : 400,
                          lineHeight: 1,
                          height: 6,
                          display: "block",
                        }}
                      >
                        ▲
                      </span>
                      <span
                        style={{
                          color:
                            sortState.key === col.key &&
                            sortState.order === "descend"
                              ? "#fff"
                              : "#ffd6c2",
                          fontWeight:
                            sortState.key === col.key &&
                            sortState.order === "descend"
                              ? 700
                              : 400,
                          lineHeight: 1,
                          height: 6,
                          display: "block",
                        }}
                      >
                        ▼
                      </span>
                    </span>
                  )}
                </span>
              </div>
            )
          )}
        </div>
      </div>
    );

  // Cell renderer for Grid
  const Cell = ({ columnIndex, rowIndex, style }) => {
    const col = columnsWithSelection[columnIndex];
    if (!col || col.isSpacer) return null;
    const row = data[rowIndex];
    if (col.key === "selection") {
      const key = typeof rowKey === "function" ? rowKey(row) : row[rowKey];
      const checked = selectedRow.includes(key);
      return (
        <div
          style={{
            ...style,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderBottom: "1px solid #f0f0f0",
            background: rowIndex % 2 === 0 ? "#fff" : "#fafafa",
            boxSizing: "border-box",
            padding: "0 12px",
            height: 40,
            minHeight: 40,
          }}
        >
          <input
            type="checkbox"
            checked={checked}
            style={{
              width: 16,
              height: 16,
              accentColor: "#F95C19",
              verticalAlign: "middle",
            }}
            onChange={() => {
              let newSelectedRowKeys;
              if (checked) {
                newSelectedRowKeys = selectedRow.filter((k) => k !== key);
              } else {
                newSelectedRowKeys = [...selectedRow, key];
              }
              setSelectedRow(newSelectedRowKeys);
              calculateTotalPrice(newSelectedRowKeys);
            }}
          />
        </div>
      );
    }
    // Always use customCellRender for non-selection columns
    const custom = customCellRender(col, row, rowIndex);
    const isStickyActionColumn = col.key === "operation"; // Your Action column key
    return (
      <div
        style={{
          ...style,
          display: "flex",
          alignItems: "center",
          borderBottom: "1px solid #f0f0f0",
          background: rowIndex % 2 === 0 ? "#fff" : "#fafafa",
          boxSizing: "border-box",
          padding: "0 10px",
          height: 40,
          minHeight: 40,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontSize: 14,
          letterSpacing: 0.1,
          transition: "background 0.2s",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = "#fff3eb")}
        onMouseLeave={(e) =>
          (e.currentTarget.style.background =
            rowIndex % 2 === 0 ? "#fff" : "#fafafa")
        }
      >
        {custom}
      </div>
    );
  };

  Cell.propTypes = {
    columnIndex: PropTypes.number.isRequired,
    rowIndex: PropTypes.number.isRequired,
    style: PropTypes.object.isRequired,
  };

  console.log("VirtualizedTable loading:", loading);

  // Handler to sync custom scrollbar with grid
  const handleCustomScrollbarScroll = (newScrollTop) => {
    if (gridRef.current && gridRef.current.scrollTo) {
      gridRef.current.scrollTo({ scrollTop: newScrollTop });
    }
  };

  // Handler to sync custom horizontal scrollbar with grid and header
  const handleCustomHorizontalScrollbarScroll = (newScrollLeft) => {
    const maxScrollLeft = Math.max(0, totalTableWidth - containerWidth);
    const clampedScrollLeft = Math.max(
      0,
      Math.min(newScrollLeft, maxScrollLeft)
    );
    if (gridRef.current && gridRef.current.scrollTo) {
      gridRef.current.scrollTo({ scrollLeft: clampedScrollLeft });
    }
    if (headerRef.current) {
      headerRef.current.style.transform = `translateX(-${clampedScrollLeft}px)`;
    }
  };

  return (
    <ResizeObserver
      onResize={({ width: newWidth }) => setContainerWidth(newWidth)}
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          minHeight: height,
          height,
          background: "#fff",
          border: "1px solid #f0f0f0",
          borderRadius: 4,
          overflow: "hidden",
          paddingBottom: 14, // reserve space for horizontal scrollbar
        }}
      >
        {loading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height,
              background: "rgba(0,0,0,0.2)",
              zIndex: 1000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "all",
            }}
          >
            <Spin />
          </div>
        )}
        {renderHeader()}
        <Grid
          ref={gridRef}
          columnCount={columnCount}
          columnWidth={getColumnWidth}
          height={height - headerHeight}
          rowCount={rowCount}
          rowHeight={getRowHeight}
          width={containerWidth}
          onScroll={({ scrollLeft, scrollTop }) => {
            const maxScrollLeft = Math.max(0, totalTableWidth - containerWidth);
            const clampedScrollLeft = Math.max(
              0,
              Math.min(scrollLeft, maxScrollLeft)
            );
            setScrollPosition &&
              setScrollPosition({ left: clampedScrollLeft, top: scrollTop });
            if (headerRef.current) {
              headerRef.current.style.transform = `translateX(-${clampedScrollLeft}px)`;
            }
          }}
        >
          {Cell}
        </Grid>
        {/* Custom always-visible vertical scrollbar overlay */}
        <VerticalScrollbar
          height={height - headerHeight}
          contentHeight={contentHeight}
          scrollTop={scrollPosition.top}
          onScroll={handleCustomScrollbarScroll}
        />
        {/* Absolutely position the horizontal scrollbar at the bottom */}
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: 10,
            zIndex: 30,
            background: "transparent",
          }}
        >
          <div style={{ pointerEvents: "auto", height: "100%" }}>
            <HorizontalScrollbar
              width={containerWidth}
              contentWidth={totalTableWidth}
              scrollLeft={scrollPosition.left}
              onScroll={handleCustomHorizontalScrollbarScroll}
              height={10}
            />
          </div>
        </div>
      </div>
    </ResizeObserver>
  );
};

VirtualizedTable.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  selectedRow: PropTypes.array.isRequired,
  setSelectedRow: PropTypes.func.isRequired,
  rowKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  height: PropTypes.number,
  width: PropTypes.number,
  rowHeight: PropTypes.number,
  headerHeight: PropTypes.number,
  onRowClick: PropTypes.func,
  customCellRender: PropTypes.func,
  loading: PropTypes.bool,
  calculateTotalPrice: PropTypes.func,
  sortState: PropTypes.object.isRequired,
  setSortState: PropTypes.func.isRequired,
  gridRef: PropTypes.object,
  setScrollPosition: PropTypes.func,
  scrollPosition: PropTypes.object,
};
const AddRoutePackage = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const latestSearchTerm = useRef("");
  const { driverState, setDriverState } = useRouteNameChanges();
  const [driverForm] = Form.useForm();
  const navigate = useNavigate();
  const { Search } = Input;
  const auth_package_columns = authUserData()?.packages_columns;
  const [selectedRow, setSelectedRow] = useState([]);
  const [loading, setLoading] = useState(false);
  const [endDate, setEndDate] = useState(null);
  const [loadingButton, setLoadingButton] = useState(false);
  const [employeeList, setEmployeeList] = useState([]);
  const [totalCount, settotalCount] = useState(0);
  const [reload, setReload] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [checkedRouted, setCheckedRouted] = useState("False");
  const [sortState, setSortState] = useState({ key: null, order: null });
  const [newPackageAdded, setNewPackageAdded] = useState(false);
  const [packageId, setpackageId] = useState(null);
  const [zones, setZones] = useState([]);
  const [loadingSuburb, setLoadingSuburb] = useState(false);
  const [loadingPostal, setLoadingPostal] = useState(false);
  const [deliverydate, setdeliverydate] = useState(null);
  const [suburbList, setSuburbList] = useState([]);
  const [postalList, setPostalList] = useState([]);
  const [states, setStates] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [driverList, setDriverList] = useState([]);
 const [companyList, setCompanyList] = useState([]);
  const [popConfirmVisible, setPopConfirmVisible] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isModalVisibleRoute, setIsModalVisibleRoute] = useState(false);
  const [showViewOptimizeButton, setShowViewOptimizeButton] = useState(false);
  const [showOptimizeButton, setShowOptimizeButton] = useState(false);
  const [showAddRouteButton, setShowAddRouteButton] = useState(true);

  const [selectedRecords, setSelectedRecords] = useState([]);
  const [checked, setChecked] = useState(false);
  const formRef = useRef();
  const [dropdownVisible, setDropdownVisible] = useState(false);

  const [state, setState] = useState({
    page: 1,
    ordering: "-id",
    pagesize: 300,
    search: "",
    total: 0,
  });
  const intialFilter = {
    state: "",
    zone_id: [],
    suburb_id: [],
    postcode_name: [],
    // zone:"",
    driver_id: "",
    company_id:"",
    unrouted: "False",
    assign_to: "",
  };
  const [stateFilter, setStateFilter] = useState(intialFilter);

  const [options, setOptions] = useState([]);
  const [options2, setOptions2] = useState([]);

  const initialState = {
    start_street_address: authUserData()?.pl_company_address
      ? authUserData()?.pl_company_address
      : null,
    start_suburb: authUserData()?.pl_company_suburb
      ? authUserData()?.pl_company_suburb
      : null,
    start_state: authUserData()?.pl_company_state
      ? authUserData()?.pl_company_state
      : null,
    start_postcode: authUserData()?.pl_company_postcode
      ? authUserData()?.pl_company_postcode
      : null,
    start_additional_details: authUserData()?.pl_company_additional_detail
      ? authUserData()?.pl_company_additional_detail
      : null,

    end_street_address: null,
    end_suburb: null,
    end_state: null,
    end_postcode: null,
    end_additional_details: null,
    packages: selectedRow,
    driver: null,
    route_name: "",
    created_at: moment(),
  };
  const [address, setAddress] = useState(initialState);
  const [checkedDisable, setCheckedDisable] = useState(false);
  const [formValid, setFormValid] = useState(false);
  const handleFormChange = (values) => {
    const isValid = Object.values(values).every(
      (value) => value !== undefined && value !== ""
    );
    setFormValid(isValid); // Update form validation state
  };

  useEffect(() => {
    if (
      auth_package_columns.length > 0 &&
      !(
        auth_package_columns.length === 1 &&
        auth_package_columns[0] === "operation"
      )
    ) {
      setCheckedList(auth_package_columns);
    }
  }, []);
  useEffect(() => {
    let canceled = false;
    const getEmployeeList = async () => {
      setLoading(true);

      // Merge state and stateFilter objects
      const ordering =
        sortState.key && sortState.order
          ? (sortState.order === "descend" ? "-" : "") + sortState.key
          : "-id";
      const queryParamsObject = {
        ...state,
       
        // created_date: startDate || null,
        // is_checked: checked || null,
        state: stateFilter?.state || null,
        driver_id: stateFilter?.driver_id || null,
        company_id: stateFilter?.company_id || null,
        unrouted: "False",
        page: state.page,
        ordering, // <-- use the computed ordering
        pagesize: state.pagesize,
        search: state.search,
        total: state.total,
        assign_to: !checked ? "" : "unassigned",
     
        ...( authUserData()?.group[0]?.name==="Supervisor" ? { is_supervisor: true } : {}),
      };

      // Filter out null or undefined values
      const queryParams = new URLSearchParams(
        Object.entries(queryParamsObject).reduce((acc, [key, value]) => {
          if (value !== null && value !== undefined) {
            acc[key] = value;
          }
          return acc;
        }, {})
      ).toString();

      const payload = {
        zone_id: stateFilter?.zone_id || [],
        suburb_id: stateFilter?.suburb_id || [],
        postcode_name: stateFilter?.postcode_name || [],
       
           start_date: startDate || null,
        end_date: endDate || null,
      
      };

      try {
        const apiResponse = await patchRequest(
          `package-model/package-by-driver-and-unassign/?is_checked=true&${queryParams}`,
          payload
        );
        if (canceled || state.search !== latestSearchTerm.current) return;
        if (apiResponse.data.results.success) {
          setEmployeeList(apiResponse.data.results.data);
          settotalCount(apiResponse.data.count);
        }
      } catch (error) {
        ErrorNotificationMsg(t("Error Fetching Package List"));
      } finally {
     
        if (!canceled) setLoading(false);
      }
    };

    getEmployeeList();
    // Cleanup function to mark this effect as canceled
    return () => {
      canceled = true;
    };
  }, [state, newPackageAdded, reload, t, stateFilter?.driver_id, sortState]);

  useEffect(() => {
    authUserData()?.country
      ? getStateByCountry(authUserData()?.country)
      : getStateByCountry(1);
    authUserData()?.state
      ? getzoneByState(authUserData()?.state)
      : getzoneByState(1);

    getDriverName();
    getCompanyName();
  }, []);
  const [checkedList, setCheckedList] = useState([
    "parcel_no",
    "created_at",
    "is_scanned",
    "suburb",
    "status",
    "zone",
    "state",
    "unrouted",
    "postcode",
    "assigned_to",
    "hawb",
    "customer_code",
    "customer_name",
    "company",
    "customer_address",
    "description_of_goods",
    "count",
    "landed",
    "weight",
    "shipper_code",
    "shipper_name",
    "shipper_address",
    "shipper_city",
    "shipper_state",
    "shipper_postcode",
    "shipper_country",
    "origin",
    "port",
    "total_value",
    "goods_currency",
    "sac",
    "fr_reference",
    "sub_master",
    "pkg_type",
    "cyber_vendor_account",
    "customs_code",
    "operation",
    "latitude",
    "longitude",
  ]);

  const generateRouteName = (driverName, date) => {
    const formattedDate = date
      ? moment(date).format("DD/MM/YYYY")
      : moment().format("DD/MM/YYYY");
    const currentTime = new Date().toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true, // Ensures the time is in 12-hour format with AM/PM
    });
    return `${driverName} - ${formattedDate}, ${currentTime}`;
  };

  const handleSearch = useCallback(
    debounce((value) => {
      const service = new window.google.maps.places.AutocompleteService();
      if (value !== "") {
        service.getPlacePredictions({ input: value }, (predictions, status) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            predictions
          ) {
            setOptions(
              predictions.map((prediction) => ({
                value: prediction.description,
                place_id: prediction.place_id,
              }))
            );
            setLoading(false);
          } else {
            setOptions([]);
            setLoading(false);
          }
        });
      } else {
        setOptions([]);
        setLoading(false);
        setAddress((prevstate) => ({
          ...prevstate,
          start_street_address: "",
          start_suburb: "",
          start_state: "",
          start_postcode: "",
        }));
        form.setFieldsValue({
          start_street_address: "",
          start_suburb: "",
          start_state: "",
          start_postcode: "",
        });
      }
    }, 300),
    []
  );
  const disabledDate = (current) => {
    // Can not select days before today
    return current && current < dayjs().startOf("day");
  };

  const handleDateChange = (date) => {
    setdeliverydate(date);
    setAddress((prevstate) => {
      const driverName =
        driverList?.find((driver) => driver.id === prevstate.driver)?.name ||
        "";
      const routeName = generateRouteName(driverName, date);

      form.setFieldsValue({ route_name: routeName });

      return {
        ...prevstate,
        created_at: date,
        route_name: routeName,
      };
    });
  };
  const handleCheckboxChangeDisbale = (isSelected) => {
    setCheckedDisable(isSelected);
  };
  const handleSearch2 = useCallback(
    debounce((value) => {
      // setLoading(true);

      const service = new window.google.maps.places.AutocompleteService();
      if (value !== "") {
        service.getPlacePredictions({ input: value }, (predictions, status) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            predictions
          ) {
            setOptions2(
              predictions.map((prediction) => ({
                value: prediction.description,
                place_id: prediction.place_id,
              }))
            );
            setLoading(false);
          } else {
            setOptions2([]);
            setLoading(false);
          }
        });
      } else {
        setOptions2([]);
        setLoading(false);
        setAddress((prevstate) => ({
          ...prevstate,
          end_street_address: "",
          end_suburb: "",
          end_state: "",
          end_postcode: "",
        }));
        form.setFieldsValue({
          end_street_address: "",
          end_suburb: "",
          end_state: "",
          end_postcode: "",
        });
      }
    }, 300),
    []
  );
  const handleSelect = (value) => {
    const selected = options.find((option) => option.value === value);
    if (selected && selected.place_id) {
      const service = new window.google.maps.places.PlacesService(
        document.createElement("div")
      );
      service.getDetails({ placeId: selected.place_id }, (place, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          place
        ) {
          setAddress((prevstate) => ({
            ...prevstate,
            start_street_address: place.formatted_address,
            start_suburb:
              place.address_components.find((ac) =>
                ac.types.includes("locality")
              )?.long_name || "",
            start_state:
              place.address_components.find((ac) =>
                ac.types.includes("administrative_area_level_1")
              )?.short_name || "",
            start_postcode:
              place.address_components.find((ac) =>
                ac.types.includes("postal_code")
              )?.long_name || "",
          }));
          form.setFieldsValue({
            start_street_address: place.formatted_address,
            start_suburb:
              place.address_components.find((ac) =>
                ac.types.includes("locality")
              )?.long_name || "",
            start_state:
              place.address_components.find((ac) =>
                ac.types.includes("administrative_area_level_1")
              )?.short_name || "",
            start_postcode:
              place.address_components.find((ac) =>
                ac.types.includes("postal_code")
              )?.long_name || "",
          });
        }
      });
    }
  };
  const handleSelect2 = (value) => {
    const selected = options2.find((option) => option.value === value);
    if (selected && selected.place_id) {
      const service = new window.google.maps.places.PlacesService(
        document.createElement("div")
      );
      service.getDetails({ placeId: selected.place_id }, (place, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          place
        ) {
          setAddress((prevstate) => ({
            ...prevstate,
            end_street_address: place.formatted_address,
            end_suburb:
              place.address_components.find((ac) =>
                ac.types.includes("locality")
              )?.long_name || "",
            end_state:
              place.address_components.find((ac) =>
                ac.types.includes("administrative_area_level_1")
              )?.short_name || "",
            end_postcode:
              place.address_components.find((ac) =>
                ac.types.includes("postal_code")
              )?.long_name || "",
          }));
          form.setFieldsValue({
            end_street_address: place.formatted_address,
            end_suburb:
              place.address_components.find((ac) =>
                ac.types.includes("locality")
              )?.long_name || "",
            end_state:
              place.address_components.find((ac) =>
                ac.types.includes("administrative_area_level_1")
              )?.short_name || "",
            end_postcode:
              place.address_components.find((ac) =>
                ac.types.includes("postal_code")
              )?.long_name || "",
          });
        }
      });
    }
  };

  const handleSubmit = async () => {
    let payload = {
      route_name: address?.route_name,
      start_street_address: address?.start_street_address,
      start_suburb: address?.start_suburb,
      start_state: address?.start_state,
      start_postcode: address?.start_postcode,
      start_additional_details: address?.start_additional_details,
      end_street_address: address?.end_street_address,
      end_suburb: address?.end_suburb,
      end_state: address?.end_state,
      end_postcode: address?.end_postcode,
      end_additional_details: address?.end_additional_details,
      packages: selectedRow ? selectedRow : [],

      driver: address?.driver,
      created_at: address?.created_at
        ? moment(address.created_at).format("DD-MM-YYYY") // Format the date here
        : null,
    };
    setLoading(true);
    setLoadingButton(true);
    try {
      let apiResponse = await postRequest(
        "route/package-list-formatted/",
        payload
      );

      if (apiResponse.data.success) {
        setAddress((prevstate) => ({
          ...prevstate,
          route_name: "",
          start_street_address: null,
          start_suburb: null,
          start_state: null,
          start_postcode: null,
          start_additional_details: null,
          end_street_address: null,
          end_suburb: null,
          end_state: null,
          end_postcode: null,
          end_additional_details: null,
          packages: [],
          driver: null,
          created_at: null,
        }));
        driverForm.resetFields();
        form.resetFields();

        const { driver, id } = apiResponse.data.route_data;

        setDriverState((prevState) => ({
          ...prevState,
          driver: driver,
          route_name: id,
        }));

        navigate(`/optimized-route`);
      } else {
        console.log("API call was not successful.");
        setLoading(false);
        setLoadingButton(false);
      }
    } catch (error) {
      console.error("Error in API call:", error);
      setLoading(false);
      setLoadingButton(false);
    }
  };
  const handleStatus = async (assignedToId, packageId, driverId) => {
    setLoading(true);

    const payload = {
      id: assignedToId,
      packages: [packageId],
      driver: driverId,
    };

    try {
      const response = await postRequest("assign-package-to-driver/", payload);

      if (response.data.success) {
        setEmployeeList((prevState) =>
          prevState.map((item) =>
            item.id === packageId
              ? {
                  ...item,
                  assigned_to_id: response?.data?.data?.id,
                  assigned_to: { ...item.assigned_to, id: driverId },
                }
              : item
          )
        );
      } else {
        console.error("Error assigning driver", response.data.message);
        // Optionally, display an error message or handle the error case
      }
    } catch (error) {
      console.error("API call failed", error);
      // Optionally, display an error message or handle the error case
    } finally {
      setLoading(false);
    }
  };

  const [columns, setColumns] = useState([]);

  const allColumns = [
    { key: "parcel_no", title: "Parcel Number" },
    { key: "created_at", title: "Schedule Date" },
    { key: "is_scanned", title: "Is Scanned" },
    { key: "status", title: "Status" },
    { key: "unrouted", title: "Unrouted" },
    { key: "suburb", title: "Suburb" },
    { key: "zone", title: "Zone" },
    { key: "state", title: "State" },
    { key: "latitude", title: "Latitude" },
    { key: "longitude", title: "Longitude" },
    { key: "postcode", title: "Postcode" },
    { key: "assigned_to", title: "Assigned Driver" },
    { key: "hawb", title: "HAWB" },
    { key: "customer_code", title: "Customer Code" },
    { key: "customer_name", title: "Customer Name" },
    { key: "company", title: "Company" },
    { key: "customer_address", title: "Customer Address" },
    { key: "description_of_goods", title: "Description of Goods" },
    { key: "count", title: "Count" },
    { key: "landed", title: "Landed" },
    { key: "weight", title: "Weight" },
    { key: "shipper_code", title: "Shipper Code" },
    { key: "shipper_name", title: "Shipper Name" },
    { key: "shipper_address", title: "Shipper Address" },
    { key: "shipper_city", title: "Shipper City" },
    { key: "shipper_state", title: "Shipper State" },
    { key: "shipper_postcode", title: "Shipper Postcode" },
    { key: "shipper_country", title: "Shipper Country" },
    { key: "origin", title: "Origin" },
    { key: "port", title: "Port" },
    { key: "total_value", title: "Total Value" },
    { key: "goods_currency", title: "Goods Currency" },
    { key: "sac", title: "SAC" },
    { key: "fr_reference", title: "FR Reference" },
    { key: "sub_master", title: "Sub Master" },
    { key: "pkg_type", title: "Package Type" },
    { key: "cyber_vendor_account", title: "Cyber Vendor Account" },
    { key: "customs_code", title: "Customs Code" },
    { key: "operation", title: "Action" },
  ];
  useEffect(() => {
    const updatedColumns = [
      // Define columns here
      {
        title: "Parcel Number",
        dataIndex: "parcel_no",
        key: "parcel_no",
        sorter: true,
        width: 100,
      },
      {
        title: "HAWB",
        dataIndex: "hawb",
        key: "hawb",
        sorter: true,
        width: 150,
      },

      {
        title: "Schedule Date",
        dataIndex: "created_at",
        key: "created_at",
        sorter: true,
        width: 150,
        render: (text) => (
          // <Tooltip title={text}>
          <div className="text-ellipsis">
            {text ? moment(text, "DD-MM-YYYY HH:mm").format("DD-MM-YYYY") : "-"}
          </div>
          // </Tooltip>
        ),
      },
      {
        title: "Is Scanned",
        dataIndex: "is_scanned",
        key: "is_scanned",
        sorter: true,
        width: 120,
        render: (text) => (
          <div className="text-ellipsis">
            {text ? (
              <img
                src={scan_check_green} // Replace with the actual path to the 'Yes' image
                alt="Yes"
                style={{ width: 20, height: 20, Color: "rgba(0, 128, 0, 0.5)" }}
              />
            ) : (
              <img
                src={scan_unchecked_red} // Replace with the actual path to the 'No' image
                alt="No"
                style={{ width: 20, height: 20 }}
              />
            )}
          </div>
        ),
      },
      {
        title: "Unrouted",
        dataIndex: "unrouted",
        key: "unrouted",
        sorter: true,
        width: 180,
        render: (text) => (
          <div className="text-ellipsis">
            {text ? (
              <img
                src={wrongicon} // Replace with the actual path to the 'Yes' image
                alt="Yes"
                style={{ width: 20, height: 20, Color: "rgba(0, 128, 0, 0.5)" }}
              />
            ) : (
              // />
              "-"
            )}
          </div>
        ),
      },
      {
        title: "Suburb",
        dataIndex: "suburb_name",
        key: "suburb",
        sorter: true,
        width: 180,
      },
      {
        title: "Latitude",
        dataIndex: "coordinates",
        key: "latitude",
        sorter: true,
        render: (coordinates) => coordinates?.latitude || "N/A",
        width: 150,
      },
      {
        title: "Longitude",
        dataIndex: "coordinates",
        key: "longitude",
        sorter: true,
        render: (coordinates) => coordinates?.longitude || "N/A",
        width: 150,
      },
      {
        title: "Run",
        dataIndex: "zone_name",
        key: "zone",
        sorter: true,
        width: 180,
      },
      {
        title: "State",
        dataIndex: "state_name",
        key: "state",
        sorter: true,
        width: 150,
      },
      {
        title: "Postcode",
        dataIndex: "postcode_name",
        key: "postcode",
        sorter: true,
        width: 150,
      },
      {
        title: "Company",
        dataIndex: "company_name",
        key: "company",
        sorter: true,
        width: 150,
      },

      {
        title: "Assigned Driver",
        dataIndex: "assigned_to",
        key: "assigned_to",
        sorter: true,
        width: 150,
        render: (assigned_to, record) =>
          driverList?.length > 0 ? (
            <Select
              className="gridselect"
              onChange={(value) =>
                handleStatus(record.assigned_to_id, record.id, value)
              }
              showArrow
              placeholder="Select Driver"
              value={
                assigned_to === "unassigned" ? "unassigned" : assigned_to?.id
              }
              disabled
            >
              {driverList?.map((driver) => (
                <Option key={driver.id} value={driver.id}>
                  {driver.name}
                </Option>
              ))}
            </Select>
          ) : (
            <span>Loading...</span>
          ),
      },

      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        sorter: true,
        width: 200,
      },
      {
        title: "Customer Code",
        dataIndex: "customer_code",
        key: "customer_code",
        sorter: true,
        width: 150,
      },
      {
        title: "Customer Name",
        dataIndex: "customer_name",
        key: "customer_name",
        sorter: true,
        width: 150,
      },
      {
        title: "Customer Address",
        dataIndex: "customer_address",
        key: "customer_address",
        sorter: true,
        width: 200,
        render: (dataIndex) => (
          <div
            style={{
              maxWidth: "400px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {dataIndex}
          </div>
        ),
      },
      {
        title: "Description of Goods",
        dataIndex: "description_of_goods",
        key: "description_of_goods",
        sorter: true,
        width: 150,
      },
      {
        title: "Count",
        dataIndex: "count",
        key: "count",
        sorter: true,
        width: 100,
      },
      {
        title: "Landed",
        dataIndex: "landed",
        key: "landed",
        sorter: true,
        width: 100,
        render: (text) => (
          <div className="text-ellipsis">{text ? "Yes" : "No"}</div>
        ),
      },
      {
        title: "Weight",
        dataIndex: "weight",
        key: "weight",
        sorter: true,
        width: 100,
      },
      {
        title: "Shipper Code",
        dataIndex: "shipper_code",
        key: "shipper_code",
        sorter: true,
        width: 150,
      },
      {
        title: "Shipper Name",
        dataIndex: "shipper_name",
        key: "shipper_name",
        sorter: true,
        width: 150,
        render: (dataIndex) => (
          <div
            style={{
              maxWidth: "400px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {dataIndex}
          </div>
        ),
      },
      {
        title: "Shipper Address",
        dataIndex: "shipper_address",
        key: "shipper_address",
        sorter: true,
        width: 200,
        render: (dataIndex) => (
          <div
            style={{
              maxWidth: "400px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {dataIndex}
          </div>
        ),
      },
      {
        title: "Shipper City",
        dataIndex: "shipper_city",
        key: "shipper_city",
        sorter: true,
        width: 150,
      },
      {
        title: "Shipper State",
        dataIndex: "shipper_state",
        key: "shipper_state",
        sorter: true,
        width: 150,
      },
      {
        title: "Shipper Postcode",
        dataIndex: "shipper_postcode",
        key: "shipper_postcode",
        sorter: true,
        width: 150,
      },
      {
        title: "Shipper Country",
        dataIndex: "shipper_country",
        key: "shipper_country",
        sorter: true,
        width: 150,
      },
      {
        title: "Origin",
        dataIndex: "origin",
        key: "origin",
        sorter: true,
        width: 150,
        render: (dataIndex) => (
          <div
            style={{
              maxWidth: "400px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {dataIndex}
          </div>
        ),
      },
      {
        title: "Port",
        dataIndex: "port",
        key: "port",
        sorter: true,
        width: 150,
      },
      {
        title: "Total Value",
        dataIndex: "total_value",
        key: "total_value",
        sorter: true,
        width: 150,
      },
      {
        title: "Goods Currency",
        dataIndex: "goods_currency",
        key: "goods_currency",
        sorter: true,
        width: 150,
      },
      {
        title: "SAC",
        dataIndex: "sac",
        key: "sac",
        sorter: true,
        width: 100,
        render: (text) => (
          <div className="text-ellipsis">{text ? "Yes" : "No"}</div>
        ),
      },
      {
        title: "FR Reference",
        dataIndex: "fr_reference",
        key: "fr_reference",
        sorter: true,
        width: 150,
      },
      {
        title: "Sub Master",
        dataIndex: "sub_master",
        key: "sub_master",
        sorter: true,
        width: 150,
      },
      {
        title: "Package Type",
        dataIndex: "pkg_type",
        key: "pkg_type",
        sorter: true,
        width: 100,
      },
      {
        title: "Cyber Vendor Account",
        dataIndex: "cyber_vendor_account",
        key: "cyber_vendor_account",
        sorter: true,
        width: 150,
      },
      {
        title: "Customs Code",
        dataIndex: "customs_code",
        key: "customs_code",
        sorter: true,
        width: 150,
      },
      {
        title: "Action",
        key: "operation",
        fixed: "right",
        width: 100,
        render: (text, record) => (
          <Space>
            {authUserData()?.permission.includes("package|change_route") && (
              <Link to={`/add-route/edit/${btoa(record.id)}`}>
                <Editicon onClick={() => handleEdit(record.id)} />
              </Link>
            )}

            {authUserData()?.permission.includes("package|delete_route") && (
              <Popconfirm
                className="action"
                title={t("pop_title")}
                okText={t("pop_ok")}
                cancelText={t("pop_cancel")}
                onConfirm={() => handleArchive(record.id)}
              >
                <Archive style={{ cursor: "pointer" }} />
              </Popconfirm>
            )}
          </Space>
        ),
      },
    ].filter((column) => checkedList.includes(column.key));
    setColumns(updatedColumns);
  }, [checkedList, driverList]);
  const calculateTotalPrice = (Ids) => {
    let selectedIds = [...Ids];
    if (!selectedIds || selectedIds.length === 0) {
      setTotalPrice(0);
      return;
    }

    const selectedPackages = employeeList.filter((pkg) =>
      selectedIds.includes(pkg.id)
    );

    const total = selectedPackages.reduce((sum, pkg) => {
      const basePrice = pkg.price || 0;
      return sum + basePrice * 1.1; // Add 10% GST
    }, 0);
    setTotalPrice(total);
  };
  const handleColumnVisibilityChange = (checkedValues) => {
    setCheckedList(checkedValues);
  };
  const columnMenu = (
    <Menu className="packagelistdropdown">
      <Menu.Item>
        <Checkbox.Group
          value={checkedList}
          onChange={handleColumnVisibilityChange}
        >
          <Row>
            {allColumns.map((column) => (
              <Col span={12} key={column.key}>
                <Checkbox
                  value={column.key}
                  disabled={column.key === "operation"}
                >
                  {column.title}
                </Checkbox>
              </Col>
            ))}
          </Row>
        </Checkbox.Group>
      </Menu.Item>
    </Menu>
  );

  // Function to convert parcel_no to string and trim it
  const getTrimmedParcelNo = (parcel_no) => {
    return parcel_no ? parcel_no?.toString().trim() : "";
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
  };

  const getDriverName = async () => {
    try {
      const response = await getRequest("employee/drivers-list/");
      const { data } = response.data;

      setDriverList(data);
      formRef.current.setFieldsValue(response.data.data);
    } catch (error) {
      // setapiLoading(false);
    }
  };
  const getCompanyName = async () => {
    try {
      // setapiLoading(true);
      const response = await getRequest("company/?no_pagination=true");

      const { data } = response.data;
      setCompanyList(data);
      formRef.current.setFieldsValue(response.data.data);
      // setapiLoading(false);
    } catch (error) {
      // setapiLoading(false);
    }
  };
  const handleAssignDriver = async (driverId = "", id = "") => {
    let ids = selectedRow;
    if (ids.length === 0) {
      ids.push(id);
    }
    try {
      let apiResponse = await patchRequest("assign-package-to-driver/update/", {
        packages: ids,
        driver: driverId,
      });
      if (apiResponse.data.success) {
        let totalArchive = totalCount - ids.length;
        if (totalArchive < 11) {
          setState((prevState) => ({
            ...prevState,
            page: 1,
            search: "",
          }));
          form.setFieldsValue({ search: "" });
          latestSearchTerm.current = "";
          setReload(!reload);
        } else {
          setReload(!reload);
        }
        SuccessNotificationMsg(t("package assigned successfully"));
        setSelectedRow([]);
        driverForm.resetFields(); // Reset only the driver form
      } else {
        setState((prevState) => ({
          ...prevState,
          page: 1,
          search: "",
        }));
      }
      form.setFieldsValue({ search: "" });
      driverForm.resetFields(); // Reset only the driver form
      latestSearchTerm.current = "";
    } catch (error) {
      setState((prevState) => ({
        ...prevState,
        page: 1,
        search: "",
      }));
    }
    driverForm.resetFields(); // Reset only the driver form
    latestSearchTerm.current = "";
    setPopConfirmVisible(false);
  };

  const handleDriverFilter = async (value) => {
    setStateFilter((prevState) => ({
      ...prevState,
      driver_id: value,
    }));
    try {
      const apiResponse = await getRequest(`employee/${value}/`);

      if (apiResponse.data.success) {
        const selectedWeight = driverList?.find((zone) => zone.id === value);
        const weightName = selectedWeight ? selectedWeight.name : "";
        const selectedDriver = driverList?.find(
          (driver) => driver.id === value
        );
        const driverName = selectedDriver ? selectedDriver.name : "";

        const routeName = generateRouteName(driverName, address?.created_at);

        setAddress((prevstate) => ({
          ...prevstate,
          route_name: routeName,
          driver: value,
          end_additional_details: apiResponse?.data?.data?.additional_details,
          end_postcode: apiResponse?.data?.data?.postcode,
          end_state: apiResponse?.data?.data?.state,
          end_suburb: apiResponse?.data?.data?.suburb,
          end_street_address: apiResponse?.data?.data?.street_address,

          // packages:selectedRow,
        }));

        form.setFieldsValue({
          route_name: routeName,
          end_additional_details: apiResponse?.data?.data?.additional_details,
          end_postcode: apiResponse?.data?.data?.postcode,
          end_state: apiResponse?.data?.data?.state,
          end_suburb: apiResponse?.data?.data?.suburb,
          end_street_address: apiResponse?.data?.data?.street_address,
        });
      }
    } catch (error) {
      const selectedWeight = driverList?.find((zone) => zone.id === value);
      const weightName = selectedWeight ? selectedWeight.name : "";
      const selectedDriver = driverList?.find((driver) => driver.id === value);
      const driverName = selectedDriver ? selectedDriver.name : "";

      const routeName = generateRouteName(driverName, address?.created_at);

      setAddress((prevstate) => ({
        ...prevstate,
        route_name: routeName,
        driver: value,
      }));

      form.setFieldsValue({ route_name: routeName });
    }
  };

  const getStateByCountry = async (id) => {
    const countryId = id || "";

    // Make the API request with the appropriate country_id
    const stateResponse = await getRequest(
      `state/?no_pagination=true&country_id=${countryId}`
    );

    // Check if the response contains the expected data and update states
    if (stateResponse?.data?.data) {
      setStates(stateResponse.data.data);
    } else {
      // Optionally, handle the case when the response doesn't contain valid data
      setStates([]);
    }
  };
  const handleStateSelectChange = (value) => {
    setStateFilter((prevState) => ({
      ...prevState,
      state: value,
      zone_id: [],
      suburb_id: [],
      postcode_name: [],
      // suburb: null,
      // zone:null,
    }));

    formRef.current.setFieldsValue({
      zone: undefined,
      suburb: undefined,
      postcode: undefined,
    });
    // setZones([]);
    setSuburbList([]);
    setPostalList([]);
    setZones([]);
    getzoneByState(value);
  };

  const getzoneByState = async (id) => {
    // If id is not provided (null, undefined, or falsy), use an empty string
    const stateId = id || "";

    // Call the API with the appropriate state_id
    const zoneResponse = await getRequest(
      `zone/?no_pagination=true&state_id=${stateId}`
    );

    // Set the zones data if the response is valid
    if (zoneResponse?.data?.data) {
      setZones(zoneResponse.data.data);
    } else {
      // Optionally, handle the case when the response doesn't contain valid data
      setZones([]);
    }
  };

  const handlezoneChange = (selectedZones) => {
    setStateFilter((prevState) => ({
      ...prevState,
      zone_id: selectedZones,
      suburb_id: [],
      postcode_name: [],
    }));

    formRef.current.setFieldsValue({ suburb: undefined, postcode: undefined });
    setSuburbList([]);
    setPostalList([]);

    getPostalByZone(selectedZones);
  };

  const getPostalByZone = async (zoneIds) => {
    setLoadingPostal(true);
    setLoadingSuburb(true);
    try {
      const payload = { zone_ids: zoneIds }; // Prepare the payload with zoneId array

      // Call both APIs in parallel using Promise.all
      const [postalResponse, suburbResponse] = await Promise.all([
        patchRequest(
          "zone/zone-by-pincode-filter/?no_pagination=True",
          payload
        ),
        patchRequest("zone/zone-by-suburb-filter/?no_pagination=True", payload),
      ]);

      // Handle the response for the postal data
      if (postalResponse?.data?.success) {
        setPostalList(postalResponse?.data?.data);
      }

      // Handle the response for the suburb data
      if (suburbResponse?.data?.success) {
        setSuburbList(suburbResponse?.data?.data); // Assuming you have a function or state to handle suburb data
      }

      setLoadingPostal(false);
      setLoadingSuburb(false);
    } catch (error) {
      ErrorNotificationMsg(t("Error fetching postal or suburb data"));
      setLoadingPostal(false);
      setLoadingSuburb(false);
    }
  };

  const handlePostalChange = (value) => {
    setStateFilter((prevState) => ({
      ...prevState,
      postcode_name: value,
    }));
  };

  const handleSuburbChange = (value) => {
    setStateFilter((prevState) => ({
      ...prevState,
      suburb_id: value,
    }));
  };
  const handleCompanyFilter = (value) => {
    setStateFilter((prevState) => ({
      ...prevState,
      company_id: value,
    }));
  };

  const handleTableChange = async (pagination, filters, sorter, ordering) => {
    const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
    setState({
      ...state,
      page: pagination.current,
      pagesize: pagination.pageSize,
      ordering: orderBy,
    });

    if (selectedRow.length === 0) {
      setPopConfirmVisible(false); // Close the Popconfirm
    }
    setSelectedRow(selectedRow);
  };

  const GetSortingEnumValue = (sortField, sortOrder) => {
    let sortingEnumKey = "-id";

    if (sortField === "parcel_no" && sortOrder === "ascend") {
      sortingEnumKey = "parcel_no";
    }
    if (sortField === "parcel_no" && sortOrder === "descend") {
      sortingEnumKey = "-parcel_no";
    }
    if (sortField === "sequence_no" && sortOrder === "ascend") {
      sortingEnumKey = "sequence_no";
    }
    if (sortField === "sequence_no" && sortOrder === "descend") {
      sortingEnumKey = "-sequence_no";
    }

    if (sortField === "hawb" && sortOrder === "ascend") {
      sortingEnumKey = "hawb";
    }
    if (sortField === "hawb" && sortOrder === "descend") {
      sortingEnumKey = "-hawb";
    }
    if (sortField === "unrouted" && sortOrder === "ascend") {
      sortingEnumKey = "unrouted";
    }
    if (sortField === "unrouted" && sortOrder === "descend") {
      sortingEnumKey = "-unrouted";
    }
    if (sortField === "created_at" && sortOrder === "ascend") {
      sortingEnumKey = "created_at";
    }
    if (sortField === "created_at" && sortOrder === "descend") {
      sortingEnumKey = "-created_at";
    }

    if (sortField === "is_scanned" && sortOrder === "ascend") {
      sortingEnumKey = "is_scanned";
    }
    if (sortField === "is_scanned" && sortOrder === "descend") {
      sortingEnumKey = "-is_scanned";
    }
    if (sortField === "latitude" && sortOrder === "ascend") {
      sortingEnumKey = "latitude";
    }
    if (sortField === "latitude" && sortOrder === "descend") {
      sortingEnumKey = "-latitude";
    }
    if (sortField === "longitude" && sortOrder === "ascend") {
      sortingEnumKey = "longitude";
    }
    if (sortField === "longitude" && sortOrder === "descend") {
      sortingEnumKey = "-longitude";
    }
    if (sortField === "suburb" && sortOrder === "ascend") {
      sortingEnumKey = "suburb__area";
    }
    if (sortField === "suburb" && sortOrder === "descend") {
      sortingEnumKey = "-suburb__area";
    }
    if (sortField === "zone" && sortOrder === "ascend") {
      sortingEnumKey = "zone__zone";
    }
    if (sortField === "zone" && sortOrder === "descend") {
      sortingEnumKey = "-zone__zone";
    }
    if (sortField === "state" && sortOrder === "ascend") {
      sortingEnumKey = "state__name";
    }
    if (sortField === "state" && sortOrder === "descend") {
      sortingEnumKey = "-state__name";
    }
    if (sortField === "postcode" && sortOrder === "ascend") {
      sortingEnumKey = "postcode__pincode";
    }
    if (sortField === "postcode" && sortOrder === "descend") {
      sortingEnumKey = "-postcode__pincode";
    }
    if (sortField === "assigned_to" && sortOrder === "ascend") {
      sortingEnumKey = "assign_package__driver__name";
    }
    if (sortField === "assigned_to" && sortOrder === "descend") {
      sortingEnumKey = "-assign_package__driver__name";
    }
    if (sortField === "status" && sortOrder === "ascend") {
      sortingEnumKey = "status";
    }
    if (sortField === "status" && sortOrder === "descend") {
      sortingEnumKey = "-status";
    }
    if (sortField === "customer_code" && sortOrder === "ascend") {
      sortingEnumKey = "customer_code";
    }
    if (sortField === "customer_code" && sortOrder === "descend") {
      sortingEnumKey = "-customer_code";
    }
    if (sortField === "customer_name" && sortOrder === "ascend") {
      sortingEnumKey = "customer_name";
    }
    if (sortField === "customer_name" && sortOrder === "descend") {
      sortingEnumKey = "-customer_name";
    }
    if (sortField === "company" && sortOrder === "ascend") {
      sortingEnumKey = "company__company_name";
    }
    if (sortField === "company" && sortOrder === "descend") {
      sortingEnumKey = "-company__company_name";
    }
    if (sortField === "customer_address" && sortOrder === "ascend") {
      sortingEnumKey = "customer_address";
    }
    if (sortField === "customer_address" && sortOrder === "descend") {
      sortingEnumKey = "-customer_address";
    }
    if (sortField === "description_of_goods" && sortOrder === "ascend") {
      sortingEnumKey = "description_of_goods";
    }
    if (sortField === "description_of_goods" && sortOrder === "descend") {
      sortingEnumKey = "-description_of_goods";
    }
    if (sortField === "count" && sortOrder === "ascend") {
      sortingEnumKey = "count";
    }

    if (sortField === "count" && sortOrder === "descend") {
      sortingEnumKey = "-count";
    }
    if (sortField === "landed" && sortOrder === "ascend") {
      sortingEnumKey = "landed";
    }
    if (sortField === "landed" && sortOrder === "descend") {
      sortingEnumKey = "-landed";
    }
    if (sortField === "weight" && sortOrder === "ascend") {
      sortingEnumKey = "weight";
    }
    if (sortField === "weight" && sortOrder === "descend") {
      sortingEnumKey = "-weight";
    }
    if (sortField === "shipper_code" && sortOrder === "ascend") {
      sortingEnumKey = "shipper_code";
    }
    if (sortField === "shipper_code" && sortOrder === "descend") {
      sortingEnumKey = "-shipper_code";
    }
    if (sortField === "shipper_name" && sortOrder === "ascend") {
      sortingEnumKey = "shipper_name";
    }
    if (sortField === "shipper_name" && sortOrder === "descend") {
      sortingEnumKey = "-shipper_name";
    }
    if (sortField === "shipper_address" && sortOrder === "ascend") {
      sortingEnumKey = "shipper_address";
    }
    if (sortField === "shipper_address" && sortOrder === "descend") {
      sortingEnumKey = "-shipper_address";
    }
    if (sortField === "shipper_city" && sortOrder === "ascend") {
      sortingEnumKey = "shipper_city";
    }
    if (sortField === "shipper_city" && sortOrder === "descend") {
      sortingEnumKey = "-shipper_city";
    }
    if (sortField === "shipper_state" && sortOrder === "ascend") {
      sortingEnumKey = "shipper_state";
    }
    if (sortField === "shipper_state" && sortOrder === "descend") {
      sortingEnumKey = "-shipper_state";
    }
    if (sortField === "shipper_postcode" && sortOrder === "ascend") {
      sortingEnumKey = "shipper_postcode";
    }
    if (sortField === "shipper_postcode" && sortOrder === "descend") {
      sortingEnumKey = "-shipper_postcode";
    }
    if (sortField === "shipper_country" && sortOrder === "ascend") {
      sortingEnumKey = "shipper_country";
    }
    if (sortField === "shipper_country" && sortOrder === "descend") {
      sortingEnumKey = "-shipper_country";
    }
    if (sortField === "origin" && sortOrder === "ascend") {
      sortingEnumKey = "origin";
    }
    if (sortField === "origin" && sortOrder === "descend") {
      sortingEnumKey = "-origin";
    }
    if (sortField === "port" && sortOrder === "ascend") {
      sortingEnumKey = "port";
    }
    if (sortField === "port" && sortOrder === "descend") {
      sortingEnumKey = "-port";
    }
    if (sortField === "total_value" && sortOrder === "ascend") {
      sortingEnumKey = "total_value";
    }
    if (sortField === "total_value" && sortOrder === "descend") {
      sortingEnumKey = "-total_value";
    }
    if (sortField === "goods_currency" && sortOrder === "ascend") {
      sortingEnumKey = "goods_currency";
    }
    if (sortField === "goods_currency" && sortOrder === "descend") {
      sortingEnumKey = "-goods_currency";
    }
    if (sortField === "sac" && sortOrder === "ascend") {
      sortingEnumKey = "sac";
    }
    if (sortField === "sac" && sortOrder === "descend") {
      sortingEnumKey = "-sac";
    }
    if (sortField === "sub_master" && sortOrder === "ascend") {
      sortingEnumKey = "sub_master";
    }
    if (sortField === "sub_master" && sortOrder === "descend") {
      sortingEnumKey = "-sub_master";
    }
    if (sortField === "pkg_type" && sortOrder === "ascend") {
      sortingEnumKey = "pkg_type";
    }
    if (sortField === "pkg_type" && sortOrder === "descend") {
      sortingEnumKey = "-pkg_type";
    }
    if (sortField === "cyber_vendor_account" && sortOrder === "ascend") {
      sortingEnumKey = "cyber_vendor_account";
    }
    if (sortField === "cyber_vendor_account" && sortOrder === "descend") {
      sortingEnumKey = "-cyber_vendor_account";
    }
    if (sortField === "customs_code" && sortOrder === "ascend") {
      sortingEnumKey = "customs_code";
    }
    if (sortField === "customs_code" && sortOrder === "descend") {
      sortingEnumKey = "-customs_code";
    }
    if (sortField === "fr_reference" && sortOrder === "ascend") {
      sortingEnumKey = "fr_reference";
    }
    if (sortField === "fr_reference" && sortOrder === "descend") {
      sortingEnumKey = "-fr_reference";
    }
    return sortingEnumKey;
  };
  const handleReset = () => {
    setState({
      ...state,
      search: "",
      pagesize: state?.pagesize,
    });

    form.setFieldsValue({ search: "" });
    latestSearchTerm.current = "";
  };

  const handleApplyFilter = async () => {
    setState({
      // ...state,
      page: 1,
      ordering: "-id",
      // pagesize: 10,
      pagesize: state?.pagesize,
      search: "",
      total: 0,
    });
    form.setFieldsValue({ search: "" });
    latestSearchTerm.current = "";
  };
  // const handleStartDateChange = (date) => {
  //   // Assuming 'date' is already a Moment.js object representing both date and time
  //   const formattedDate = moment(date).format("DD-MM-YYYY");
  //   setStartDate(formattedDate);
  // };
    const handleStartDateChange = (date) => {
      if (date) {
        const formattedDate = moment(date).format("DD-MM-YYYY");
        setStartDate(formattedDate);
      } else {
        // When date is cleared
        setStartDate(null);
        // Optionally call your API to clear the date
        // clearDateFromAPI();
      }
    };
      const handleEndDateChange = (date) => {
        if (date) {
        const formattedDate = moment(date).format("DD-MM-YYYY");
        setEndDate(formattedDate);}
        else {
          // When date is cleared
          setEndDate(null);
          // Optionally call your API to clear the date
          // clearDateFromAPI();
        }
      };
        const minEndDate = startDate
          ? moment(startDate, "DD-MM-YYYY").startOf("day")
          : null;
  const handleResetFilter = () => {
    setState({
      // ...state,
      page: 1,
      ordering: "-id",
      pagesize: state?.pagesize,
      search: "",
      total: 0,
    });

    setStateFilter(intialFilter);

    form.setFieldsValue({ search: "" });
    latestSearchTerm.current = "";
    setStartDate(null);
    setEndDate(null);
    setChecked(false);
    setZones([]);
    setSuburbList([]);
    setPostalList([]);
    setZones([]);
    setCheckedRouted("False");
    // setStates([]);
    formRef.current.setFieldsValue({
      state: undefined, // Set to undefined to keep placeholder
      zone: undefined, // Set to undefined to keep placeholder
      unrouted: false,
      unassigned: false,
      // zone: undefined,   // Set to undefined to keep placeholder
      suburb: undefined, // Set to undefined to keep placeholder
      // created_date: undefined,
      start_date: undefined,
      end_date: undefined,
      driver_id: undefined,
      company_id:undefined,
      postcode: undefined,
      // suburb: response.data.results.data.country_name,
    });
  };
  const searchCallbackDelayed = (event) => {
    const { value } = event.target;
    debounceLoadData(value, onSearch);
  };

  const onSearch = (value) => {
    latestSearchTerm.current = value;
    setState({
      ...state,
      search: value,
    });
  };

  const debounceLoadData = debounce((value, onSearch) => {
    onSearch(value);
  }, 1000);
  const handleCheckboxChange = (e) => {
    setChecked(e.target.checked);
    // setState((prevState) => ({
    //   ...prevState,
    //   page: 1,
    //   search: "",
    //   pagesize: state?.pagesize,
    // }));
    // setReload(!reload);
    // form.setFieldsValue({ search: "" });
    // latestSearchTerm.current = "";
  };

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      const hasSelectedRows = selectedRows?.length > 0;
      handleCheckboxChangeDisbale(hasSelectedRows);
      const shouldEnableAddRouteButton =
        hasSelectedRows &&
        selectedRows?.every(
          (row) =>
            row?.is_scanned === true &&
            (row?.parcel_no === null || row?.parcel_no === 0)
        );
      setShowAddRouteButton(!shouldEnableAddRouteButton);
      const allRowsHaveParcelNo = selectedRows?.every(
        (row) => getTrimmedParcelNo(row?.parcel_no) !== ""
      );
      const allRowsHaveEmptyParcelNo = selectedRows?.every(
        (row) => getTrimmedParcelNo(row?.parcel_no) === ""
      );

      setShowViewOptimizeButton(allRowsHaveParcelNo);

      setShowOptimizeButton(allRowsHaveEmptyParcelNo);
    },
    onSelect: (record, selected, selectedRows) => {
      const updatedSelectedRowKeys = selected
        ? [...selectedRow, record.id]
        : selectedRow?.filter((key) => key !== record.id);
      setSelectedRow(updatedSelectedRowKeys);

      setSelectedRecords(selectedRows); // Store full record objects

      const hasSelectedRows = selectedRows?.length > 0;
      handleCheckboxChangeDisbale(hasSelectedRows);
      const shouldEnableAddRouteButton =
        hasSelectedRows &&
        selectedRows?.every(
          (row) =>
            row?.is_scanned === true &&
            (row?.parcel_no === null || row?.parcel_no === 0)
        );
      setShowAddRouteButton(!shouldEnableAddRouteButton);
      const allRowsHaveParcelNo = selectedRows?.every(
        (row) => getTrimmedParcelNo(row?.parcel_no) !== ""
      );
      const allRowsHaveEmptyParcelNo = selectedRows?.every(
        (row) => getTrimmedParcelNo(row?.parcel_no) === ""
      );

      setShowViewOptimizeButton(allRowsHaveParcelNo);

      setShowOptimizeButton(allRowsHaveEmptyParcelNo);
    },
    onSelectAll: (selected, selectedRows, changeRows) => {
      const updatedSelectedRowKeys = selected
        ? [...selectedRow, ...changeRows.map((row) => row.id)]
        : selectedRow?.filter(
            (key) => !changeRows.find((row) => row.id === key)
          );

      setSelectedRow(updatedSelectedRowKeys);
      setSelectedRecords(selectedRows); // Store full record objects

      const hasSelectedRows = selectedRows?.length > 0;
      handleCheckboxChangeDisbale(hasSelectedRows);
      const shouldEnableAddRouteButton =
        hasSelectedRows &&
        selectedRows.every(
          (row) =>
            row?.is_scanned === true &&
            (row?.parcel_no === null || row?.parcel_no === 0)
        );
      setShowAddRouteButton(!shouldEnableAddRouteButton);
      const allRowsHaveParcelNo = selectedRows?.every(
        (row) => getTrimmedParcelNo(row?.parcel_no) !== ""
      );
      const allRowsHaveEmptyParcelNo = selectedRows?.every(
        (row) => getTrimmedParcelNo(row?.parcel_no) === ""
      );

      setShowViewOptimizeButton(allRowsHaveParcelNo);

      setShowOptimizeButton(allRowsHaveEmptyParcelNo);
    },
    selectedRowKeys: selectedRow,
  };

  const handleArchive = async (id = []) => {
    let ids = [...selectedRow];
    if (ids.length === 0 && id) {
      ids.push(id);
    }
    try {
      let apiResponse = await postRequest(
        "package-model/archive-package-model/",
        {
          deleted: ids,
        }
      );
      if (apiResponse.data.success) {
        let totalArchive = totalCount - ids.length;

        if (totalArchive < 11) {
          setState((prevState) => ({
            ...prevState,
            page: 1,
            search: "",
          }));
          form.setFieldsValue({ search: "" });
          latestSearchTerm.current = "";
          setReload(!reload);
        } else {
          setReload(!reload);
        }
        SuccessNotificationMsg(t("package_archive_successfully"));

        setSelectedRow([]);
      } else {
        setState((prevState) => ({
          ...prevState,
          page: 1,
          search: "",
        }));
        form.setFieldsValue({ search: "" });
        latestSearchTerm.current = "";
      }
    } catch (error) {
      setState((prevState) => ({
        ...prevState,
        page: 1,
        search: "",
      }));
      form.setFieldsValue({ search: "" });
      latestSearchTerm.current = "";
    }
  };

  const handleEdit = (id) => {
    setpackageId(id);
  };

  // VirtualTable component for virtualization with manual selection column
  const VirtualTable = ({
    columns,
    scroll,
    dataSource,
    selectedRow,
    selectedRecords,
    setSelectedRow,
    setSelectedRecords,
    rowKey,
    loading,
    onChange,
    sticky,
  }) => {
    const [tableWidth, setTableWidth] = useState(0);
    const gridRef = useRef();
    const scrollPositionRef = useRef({ left: 0, top: 0 });

    // Memoize mergedColumns to prevent remounts
    const mergedColumns = useMemo(() => {
      let cols = [
        {
          key: "selection",
          width: 60,
          title: () => {
            const allVisibleSelected =
              dataSource.length > 0 &&
              dataSource.every((record) =>
                selectedRow.includes(
                  typeof rowKey === "function" ? rowKey(record) : record[rowKey]
                )
              );
            const someVisibleSelected = dataSource.some((record) =>
              selectedRow.includes(
                typeof rowKey === "function" ? rowKey(record) : record[rowKey]
              )
            );
            return (
              <input
                type="checkbox"
                checked={allVisibleSelected}
                ref={(el) => {
                  if (el)
                    el.indeterminate =
                      !allVisibleSelected && someVisibleSelected;
                }}
                onChange={(e) => {
                  if (e.target.checked) {
                    const newKeys = [
                      ...selectedRow,
                      ...dataSource
                        .map((record) =>
                          typeof rowKey === "function"
                            ? rowKey(record)
                            : record[rowKey]
                        )
                        .filter((key) => !selectedRow.includes(key)),
                    ];
                    const newRecords = [
                      ...selectedRecords,
                      ...dataSource.filter(
                        (record) =>
                          !selectedRow.includes(
                            typeof rowKey === "function"
                              ? rowKey(record)
                              : record[rowKey]
                          )
                      ),
                    ];
                    setSelectedRow(newKeys);

                    setSelectedRecords(newRecords);
                  } else {
                    const visibleKeys = dataSource.map((record) =>
                      typeof rowKey === "function"
                        ? rowKey(record)
                        : record[rowKey]
                    );
                    setSelectedRow(
                      selectedRow.filter((key) => !visibleKeys.includes(key))
                    );
                    setSelectedRecords(
                      selectedRecords.filter(
                        (record) =>
                          !visibleKeys.includes(
                            typeof rowKey === "function"
                              ? rowKey(record)
                              : record[rowKey]
                          )
                      )
                    );
                  }
                }}
              />
            );
          },
          render: (text, record, rowIndex) => {
            const key =
              typeof rowKey === "function" ? rowKey(record) : record[rowKey];
            const checked = selectedRow.includes(key);
            return (
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => {
                  let newSelectedRowKeys, newSelectedRows;
                  if (e.target.checked) {
                    newSelectedRowKeys = [...selectedRow, key];
                    newSelectedRows = [...selectedRecords, record];
                  } else {
                    newSelectedRowKeys = selectedRow.filter((k) => k !== key);
                    newSelectedRows = selectedRecords.filter(
                      (r) =>
                        (typeof rowKey === "function"
                          ? rowKey(r)
                          : r[rowKey]) !== key
                    );
                  }
                  setSelectedRow(newSelectedRowKeys);

                  setSelectedRecords(newSelectedRows);
                }}
              />
            );
          },
        },
        ...columns,
      ];
      const widthColumnCount = cols.filter(({ width }) => !width).length;
      cols = cols.map((column) => {
        if (column.width) return column;
        return { ...column, width: Math.floor(tableWidth / widthColumnCount) };
      });
      return cols;
    }, [
      columns,
      dataSource,
      selectedRow,
      selectedRecords,
      setSelectedRow,
      setSelectedRecords,
      rowKey,
      tableWidth,
    ]);

    useEffect(() => {
      if (gridRef.current) {
        gridRef.current.resetAfterIndices({
          columnIndex: 0,
          shouldForceUpdate: true,
        });
      }
    }, [tableWidth]);

    // Helper to get cell content, supporting custom renderers
    const getCellContent = (column, record, rowIndex) => {
      if (column.render) {
        return column.render(record[column.dataIndex], record, rowIndex);
      }
      return record[column.dataIndex];
    };

    // Ref callback for Grid to restore scroll position on mount
    const gridRefCallback = (node) => {
      if (node) {
        console.log("node", node);
        console.log("scroll-left", scrollPositionRef);
        console.log("scroll-top", scrollPositionRef.current.top);
        gridRef.current = node;
        if (typeof node.scrollTo === "function") {
          node.scrollTo({
            scrollLeft: scrollPositionRef.current.left,
            scrollTop: scrollPositionRef.current.top,
          });
        }
      }
    };

    // Memoize renderVirtualList to prevent remounts
    const renderVirtualList = useCallback(
      (rawData, { scrollbarSize, ref, onScroll }) => {
        return (
          <Grid
            key={`grid-${tableWidth}-${mergedColumns.length}-${dataSource.length}`}
            ref={gridRefCallback}
            columnCount={mergedColumns.length}
            columnWidth={(index) => mergedColumns[index].width}
            height={scroll.y}
            rowCount={rawData.length}
            rowHeight={() => 54}
            width={tableWidth}
            initialScrollLeft={scrollPositionRef.current.left}
            initialScrollTop={scrollPositionRef.current.top}
            onScroll={({ scrollLeft, scrollTop }) => {
              scrollPositionRef.current = { left: scrollLeft, top: scrollTop };
              onScroll({ scrollLeft, scrollTop });
            }}
          >
            {({ columnIndex, rowIndex, style }) => (
              <div
                className="virtual-table-cell"
                // style={{
                //   ...style,
                //   boxSizing: "border-box",
                //   padding: "8px",
                //   borderBottom: "1px solid #f0f0f0",
                //   background: rowIndex % 2 === 0 ? "#fff" : "#fafafa",
                // }}
              >
                {getCellContent(
                  mergedColumns[columnIndex],
                  rawData[rowIndex],
                  rowIndex
                )}
              </div>
            )}
          </Grid>
        );
      },
      [mergedColumns, scroll.y, tableWidth, dataSource.length]
    );

    return (
      <ResizeObserver
        onResize={({ width }) => {
          setTableWidth(width);
        }}
      >
        <Table
          columns={mergedColumns}
          dataSource={dataSource}
          rowSelection={undefined} // handled manually
          rowKey={rowKey}
          loading={loading}
          pagination={false}
          components={{
            body: renderVirtualList,
          }}
          scroll={scroll}
          onChange={onChange}
          sticky={sticky}
        />
      </ResizeObserver>
    );
  };

  VirtualTable.propTypes = {
    columns: PropTypes.array.isRequired,
    scroll: PropTypes.object.isRequired,
    dataSource: PropTypes.array.isRequired,
    selectedRow: PropTypes.array.isRequired,
    selectedRecords: PropTypes.array.isRequired,
    setSelectedRow: PropTypes.func.isRequired,
    setSelectedRecords: PropTypes.func.isRequired,
    rowKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
    loading: PropTypes.bool,
    onChange: PropTypes.func,
    sticky: PropTypes.bool,
  };

  // Prepare columns for VirtualizedTable (add selection column at start)
  const virtualColumns = useMemo(
    () => [
      { key: "selection", title: "", width: 50 },
      ...columns.map((col) => ({ ...col, width: col.width || 120 })),
    ],
    [columns]
  );

  // Update customCellRender to handle blank data and custom renderers correctly
  const customCellRender = useCallback((col, row, rowIndex) => {
    if (col.key === "selection") return null; // handled separately
    if (col.render) {
      // If the column has a render function, use it
      return col.render(
        col.dataIndex ? row[col.dataIndex] : undefined,
        row,
        rowIndex
      );
    }
    // Otherwise, just show the value
    return col.dataIndex ? row[col.dataIndex] : null;
  }, []);

  // Add before the main render/return of PackageList
  const selectionColumn = {
    key: "selection",
    width: 60,
    title: "", // The title will be set in VirtualizedTable
  };

  const columnsWithSelection = useMemo(
    () =>
      columns[0]?.key === "selection" ? columns : [selectionColumn, ...columns],
    [columns]
  );

  // Table and scrollbar dimensions

  const headerHeight = 54;
  const rowHeight = 54;
  const [containerWidth, setContainerWidth] = useState(1200); // You can use ResizeObserver for dynamic width
  const contentHeight = employeeList.length * rowHeight;
  const totalTableWidth = columnsWithSelection.reduce(
    (sum, col) => sum + (col.width || 120),
    0
  );
  const [scrollPosition, setScrollPosition] = useState({ left: 0, top: 0 });

  // Handlers to sync scrollbars with table
  const handleCustomScrollbarScroll = (newScrollTop) => {
    if (gridRef.current && gridRef.current.scrollTo) {
      gridRef.current.scrollTo({ scrollTop: newScrollTop });
    }
  };
  const handleCustomHorizontalScrollbarScroll = (newScrollLeft) => {
    if (gridRef.current && gridRef.current.scrollTo) {
      gridRef.current.scrollTo({ scrollLeft: newScrollLeft });
    }
  };

  const colors = ["#737373"];
  const gridRef = useRef();
  const tableContainerRef = useRef(null);

  // Dynamic table height using container ref for robust last-row visibility
  const [tableHeight, setTableHeight] = useState(600);
  useEffect(() => {
    const handleResize = () => {
      if (tableContainerRef.current) {
        const containerHeight = tableContainerRef.current.offsetHeight;
        setTableHeight(containerHeight > 300 ? containerHeight : 300);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
            <h3>{t("Add Route")}</h3>
            {/* </Tooltip> */}

            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/dashboard">{t("home")}</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>{t("Add Route")}</Breadcrumb.Item>
            </Breadcrumb>
          </div>
        </div>
      </div>
      <section className="grid-sec">
        <div className="addRoute-wrp">
          <LoadScript
            googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
            libraries={libraries}
          >
            <Spin spinning={""}>
              <Form
                form={form}
                layout="vertical"
                onValuesChange={handleFormChange}
                initialValues={address}
                ref={formRef}
              >
                <Row gutter={10} className="addRoutModal">
                  <Col
                    xs={24}
                    sm={12}
                    md={8}
                    lg={12}
                    className="TextFeild gutter-row"
                  >
                    <Form form={driverForm} autoComplete="off" ref={formRef}>
                      <Form.Item
                        name="driver"
                        label={t("assign_driver")}
                        rules={[
                          {
                            required: true,
                            message: t("val_driver"),
                          },
                        ]}
                      >
                        <Select
                          placeholder={t("pl_assign_driver")}
                          showArrow
                          showSearch
                          optionFilterProp="children"
                          onChange={(value) => handleDriverFilter(value)}
                        >
                          {driverList?.map((item) => (
                            <Option key={item.id} value={item.id}>
                              {item.name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Form>
                  </Col>
                  <Col
                    xs={24}
                    sm={24}
                    md={8}
                    lg={12}
                    className="TextFeild gutter-row"
                  >
                    <Form.Item name="created_at" label={t("Date")}>
                      <DatePicker
                        placeholder={t("pl_date")}
                        format="DD-MM-YYYY"
                        showTime={false}
                        showNow={false}
                        disabledDate={disabledDate}
                        allowClear={false}
                        value={address?.created_at}
                        onChange={handleDateChange}
                      />
                    </Form.Item>
                  </Col>
                  <Col
                    xs={24}
                    sm={24}
                    md={24}
                    lg={24}
                    className="TextFeild gutter-row"
                  >
                    <Form.Item name="route_name" label={t("Route Name")}>
                      <Input
                        placeholder={t("Route")}
                        name="route_name"
                        value={address.route_name}
                        disabled
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12}>
                    <Collapse defaultActiveKey={["1"]}>
                      <Panel header="1. Start Location" key="1">
                        <Row gutter={10}>
                          <Col xs={24} md={12} className="TextFeild gutter-row">
                            <Form.Item
                              name="start_street_address"
                              label={t("street_address")}
                              rules={[
                                {
                                  required: true,
                                  message: (
                                    <Trans>{t("var_street_address")}</Trans>
                                  ),
                                },
                              ]}
                            >
                              <AutoComplete
                                id="start_street_address"
                                placeholder={t("pl_street_address")}
                                options={options}
                                onSearch={handleSearch}
                                onSelect={handleSelect}
                                value={address.start_street_address}
                                onChange={(value) => {
                                  setAddress((prevstate) => ({
                                    ...prevstate,
                                    start_street_address: value,
                                  }));
                                  form.setFieldsValue({
                                    start_street_address: value,
                                  });
                                }}
                              />
                            </Form.Item>
                          </Col>

                          <Col xs={24} md={12} className="TextFeild gutter-row">
                            <Form.Item
                              name="start_suburb"
                              label={t("pl_suburb")}
                              rules={[
                                {
                                  required: true,
                                  message: (
                                    <Trans>{t("val_select_suburb")}</Trans>
                                  ),
                                },
                              ]}
                            >
                              <Input
                                placeholder={t("pl_enter_suburb")}
                                name="start_suburb"
                                value={address.start_suburb}
                                onChange={(e) => {
                                  const { value } = e.target;
                                  setAddress((prevstate) => ({
                                    ...prevstate,
                                    start_suburb: value,
                                  }));
                                  form.setFieldsValue({
                                    start_suburb: value,
                                  });
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12} className="TextFeild gutter-row">
                            <Form.Item
                              name="start_state"
                              label={t("pl_state_name")}
                              rules={[
                                {
                                  required: true,
                                  message: <Trans>{t("val_state")}</Trans>,
                                },
                              ]}
                            >
                              <Input
                                placeholder={t("pl_enter_state")}
                                name="start_state"
                                value={address.start_state}
                                onChange={(e) => {
                                  const { value } = e.target;
                                  setAddress((prevstate) => ({
                                    ...prevstate,
                                    start_state: value,
                                  }));
                                  form.setFieldsValue({
                                    start_state: value,
                                  });
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12} className="TextFeild gutter-row">
                            <Form.Item
                              name="start_postcode"
                              label={t("pl_Postcode")}
                              rules={[
                                {
                                  required: true,
                                  message: <Trans>{t("val_Postcode")}</Trans>,
                                },
                              ]}
                            >
                              <Input
                                placeholder={t("pl_enter_postcode")}
                                name="start_postcode"
                                value={address.start_postcode}
                                onChange={(e) => {
                                  const { value } = e.target;
                                  setAddress((prevstate) => ({
                                    ...prevstate,
                                    start_postcode: value,
                                  }));
                                  form.setFieldsValue({
                                    start_postcode: value,
                                  });
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} className="TextFeild gutter-row">
                            <Form.Item
                              name="start_additional_details"
                              label={t("additional_details")}
                              rules={[
                                {
                                  required: false,
                                  message: (
                                    <Trans>
                                      {t("pl_enter_additional_detail")}
                                    </Trans>
                                  ),
                                },
                              ]}
                            >
                              <Input
                                placeholder={t("enter_additional_details")}
                                name="start_additional_details"
                                value={address.start_additional_details}
                                onChange={(e) => {
                                  const { value } = e.target;
                                  setAddress((prevstate) => ({
                                    ...prevstate,
                                    start_additional_details: value,
                                  }));
                                  form.setFieldsValue({
                                    start_additional_details: value,
                                  });
                                }}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Panel>
                    </Collapse>
                  </Col>
                  <Col xs={24} md={12}>
                    <Collapse defaultActiveKey={["2"]}>
                      <Panel
                        header="2. End Location"
                        key="2"
                        style={{ textAlign: "left" }}
                      >
                        <Row gutter={10}>
                          <Col xs={24} md={12} className="TextFeild gutter-row">
                            <Form.Item
                              name="end_street_address"
                              label={t("street_address")}
                            >
                              <AutoComplete
                                id="end_street_address"
                                placeholder={t("pl_street_address")}
                                options={options2}
                                onSearch={handleSearch2}
                                onSelect={handleSelect2}
                                value={address?.end_street_address}
                                onChange={(value) => {
                                  setAddress((prevstate) => ({
                                    ...prevstate,
                                    end_street_address: value,
                                  }));
                                  form.setFieldsValue({
                                    end_street_address: value,
                                  });
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12} className="TextFeild gutter-row">
                            <Form.Item name="end_suburb" label={t("pl_suburb")}>
                              <Input
                                placeholder={t("pl_enter_suburb")}
                                name="end_suburb"
                                value={address.end_suburb}
                                onChange={(e) => {
                                  const { value } = e.target;
                                  setAddress((prevstate) => ({
                                    ...prevstate,
                                    end_suburb: value,
                                  }));
                                  form.setFieldsValue({
                                    end_suburb: value,
                                  });
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12} className="TextFeild gutter-row">
                            <Form.Item name="end_state" label={t("pl_state")}>
                              <Input
                                placeholder={t("pl_enter_state")}
                                name="end_state"
                                value={address.end_state}
                                onChange={(e) => {
                                  const { value } = e.target;
                                  setAddress((prevstate) => ({
                                    ...prevstate,
                                    end_state: value,
                                  }));
                                  form.setFieldsValue({ end_state: value });
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12} className="TextFeild gutter-row">
                            <Form.Item
                              name="end_postcode"
                              label={t("pl_Postcode")}
                            >
                              <Input
                                placeholder={t("pl_enter_postcode")}
                                name="end_postcode"
                                value={address.end_postcode}
                                onChange={(e) => {
                                  const { value } = e.target;
                                  setAddress((prevstate) => ({
                                    ...prevstate,
                                    end_postcode: value,
                                  }));
                                  form.setFieldsValue({
                                    end_postcode: value,
                                  });
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} className="TextFeild gutter-row">
                            <Form.Item
                              name="end_additional_details"
                              label={t("additional_details")}
                            >
                              <Input
                                placeholder={t("enter_additional_details")}
                                name="end_additional_details"
                                value={address.end_additional_details}
                                onChange={(e) => {
                                  const { value } = e.target;
                                  setAddress((prevstate) => ({
                                    ...prevstate,
                                    end_additional_details: value,
                                  }));
                                  form.setFieldsValue({
                                    end_additional_details: value,
                                  });
                                }}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Panel>
                    </Collapse>
                  </Col>
                </Row>
              </Form>
            </Spin>
          </LoadScript>

          <div className="mapfilter-body">
            <Form autoComplete="off" ref={formRef} className="gridformfilter">
              {authUserData()?.permission.includes("package|view_route") && (
                <Row className="mapfilter">
                  <Col className="TextFeild gutter-row">
                    {/* <Form.Item name="created_date" label={t("pl_date")}>
                      <DatePicker
                        placeholder={t("pl_date")}
                        format="DD-MM-YYYY"
                        showTime={false}
                        allowClear={false}
                        onChange={(date, dateString) =>
                          handleStartDateChange(date, dateString)
                        }
                        style={{ width: "130px" }}
                      />
                    </Form.Item> */}
                     <Form.Item
                                          name="start_date"
                                          label={t("pl_min_date")}
                                          rules={[
                                            {
                                              required: false,
                                              message: <Trans>{t("val_start_date")}</Trans>,
                                            },
                                          ]}
                                        >
                                          <DatePicker
                                            format="DD-MM-YYYY"
                                            allowClear={false}
                                            onChange={handleStartDateChange}
                                            style={{ width: "140px" }}
                                            placeholder={t("statr_date")}
                                          />
                                        </Form.Item>
                                  
                                        <Form.Item
                                          name="end_date"
                                          label={t("pl_max_date")}
                                          rules={[
                                            {
                                              required: false,
                                              message: <Trans>{t("val_end_date")}</Trans>,
                                            },
                                          ]}
                                        >
                                          <DatePicker
                                            format="DD-MM-YYYY"
                                            allowClear={false}
                                            onChange={handleEndDateChange}
                                            style={{ width: "140px" }}
                                            placeholder={t("end_date")}
                                            disabled={!startDate}
                                            disabledDate={(current) =>
                                              current && (minEndDate ? current < minEndDate : false)
                                            }
                                          />
                                        </Form.Item>
                  </Col>

                  <Col className="TextFeild gutter-row">
                    <Form.Item name="state" label={t("pl_state")}>
                      <Select
                        name="state"
                        label={t("pl_state")}
                        placeholder={t("pl_select_state")}
                        value={state.state}
                        onChange={handleStateSelectChange}
                        showArrow
                        showSearch
                        optionFilterProp="children"
                        style={{ width: "140px" }}
                      >
                        {states.map((state) => (
                          <Option key={state.id} value={state.id}>
                            {state.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col className="TextFeild gutter-row">
                    <Form.Item name="zone" label={t("pl_zone")}>
                      <Select
                        name="zone"
                        label={t("pl_zone")}
                        placeholder={t("val_zone")}
                        onChange={handlezoneChange}
                        value={state.zone}
                        showArrow
                        mode="multiple"
                        showSearch
                        optionFilterProp="children"
                        style={{ width: "100px" }}
                      >
                        {zones?.map((item) => (
                          <Option key={item.id} value={item.id}>
                            {item.zone}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col className="TextFeild gutter-row">
                    <Form.Item name="suburb" label={t("pl_suburb")}>
                      <Select
                        placeholder={t("pl_select_suburb")}
                        name="suburb"
                        value={state.suburb}
                        showArrow
                        onChange={handleSuburbChange}
                        mode="multiple"
                        showSearch
                        optionFilterProp="children"
                        style={{ width: "170px" }}
                        loading={loadingSuburb}
                        disabled={loadingSuburb}
                      >
                        {suburbList?.map((suburb) => (
                          <Option
                            key={suburb.suburb_id}
                            value={suburb.suburb_id}
                          >
                            {suburb.suburb_name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col className="TextFeild gutter-row">
                    <Form.Item name="postcode" label={t("pl_postal")}>
                      <Select
                        placeholder={t("pl_select_postal")}
                        name="postcode"
                        value={state.postcode}
                        showArrow
                        onChange={handlePostalChange}
                        mode="multiple"
                        showSearch
                        optionFilterProp="children"
                        style={{ width: "125px" }}
                        loading={loadingPostal}
                        disabled={loadingPostal}
                      >
                        {postalList?.map((postal) => (
                          <Option
                            key={postal.postcode_name}
                            value={postal.postcode_name}
                          >
                            {postal.postcode_name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col className="TextFeild gutter-row">
                    <Form.Item name="company_id" label={t("pl_company")}>
                                            <Select
                                              placeholder={t("val_company")}
                                              showArrow
                                              showSearch
                                              style={{ width: "170px" }}
                                              name="company_id"
                                              optionFilterProp="children"
                                              onChange={(value) => handleCompanyFilter(value)}
                                              size="small"
                                            >
                                              {companyList?.map((item) => (
                                                <Option key={item.id} value={item.id}>
                                                  {item.company_name}
                                                </Option>
                                              ))}
                                            </Select>
                                          </Form.Item>
                                          </Col>
                                          <Col className="TextFeild gutter-row">
                                          <Form.Item
                        className="BrowsButtonSec"
                        label={t("Unassigned Packages")}
                        name="unassigned"
                        valuePropName="checked"
                      >
                        <Checkbox
                          onChange={handleCheckboxChange}
                          checked={checked}
                        ></Checkbox>
                      </Form.Item>
                      </Col>

                  <>
                    <span>
                      <ButtonField
                        className="BlueShadowBtn"
                        type="primary"
                        htmlType="Apply"
                        label={
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "5px",
                            }}
                          >
                            <FilterOutlined style={{ fontSize: "16px" }} />{" "}
                            Apply Filter
                          </span>
                        }
                        onClick={handleApplyFilter}
                      />
                    </span>

                    {startDate ||
                  endDate ||
                    stateFilter?.state ||
                    checkedRouted === "True" ||
                    checked||
                    stateFilter?.suburb_id.length !== 0 ||
                    stateFilter?.postcode_name.length !== 0 ||
                    stateFilter?.zone_id.length !== 0 ||
                    stateFilter?.company_id.length !== 0 ? (
                      <span>
                        <ButtonField
                          className="BlueShadowBtn"
                          type="primary"
                          htmlType="Reset"
                          label={
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "5px",
                              }}
                            >
                              <UndoOutlined
                                style={{ fontSize: "16px", color: "#fff" }}
                              />{" "}
                              Reset
                            </span>
                          }
                          onClick={handleResetFilter}
                        />
                      </span>
                    ) : (
                      ""
                    )}
                    {/* <Col className="searchwrp">
                      <Form.Item
                        className="BrowsButtonSec"
                        label={t("Unassigned Packages")}
                        name="unassigned"
                        valuePropName="checked"
                      >
                        <Checkbox
                          onChange={handleCheckboxChange}
                          checked={checked}
                        ></Checkbox>
                      </Form.Item>
                    </Col> */}
                    <Col
                      xs={24}
                      sm={24}
                      md={24}
                      lg={24}
                      xl={24}
                      className="TextFeild gutter-row"
                    ></Col>
                  </>
                  <SelectRouteModal
                    parcels={selectedRecords}
                    isModalVisible={isModalVisibleRoute}
                    selectedRow={selectedRow}
                    setIsModalVisible={setIsModalVisibleRoute}
                    setState={setState}
                  />
                </Row>
              )}
              <Space>
                <div className="searchwrp">
                  <Space direction="vertical">
                    <Form form={form}>
                      {authUserData()?.permission.includes(
                        "package|view_route"
                      ) && (
                        <Form.Item name="search" initialValue="">
                          <Search
                            prefix={
                              <img
                                src={SearchIcon}
                                alt="SearchIcon"
                                className="SearchIcon"
                                width={22}
                              />
                            }
                            placeholder={t("pl_search_list")}
                            onChange={searchCallbackDelayed}
                          />
                        </Form.Item>
                      )}
                    </Form>
                  </Space>
                </div>
                {authUserData()?.permission.includes("package|view_route") && (
                  <>
                    {state?.search ? (
                      <Button
                        className="BlueShadowBtn"
                        type="primary"
                        htmlType="button"
                        onClick={() => handleReset()}
                      >
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                        >
                          <UndoOutlined
                            style={{ fontSize: "16px", color: "#fff" }}
                          />{" "}
                          Reset
                        </span>
                      </Button>
                    ) : (
                      ""
                    )}
                  </>
                )}

                {authUserData()?.permission.includes(
                  "package|delete_route"
                ) && (
                  <>
                    {selectedRow.length > 0 && (
                      <Popconfirm
                        className="action"
                        title={t("pop_title")}
                        okText={t("pop_ok")}
                        cancelText={t("pop_cancel")}
                        placement="left"
                        onConfirm={() => handleArchive()}
                      >
                        <ButtonField
                          className="BlueShadowBtn"
                          type="primary"
                          htmlType="button"
                          label={
                            <>
                              <div
                                className="BlueShadowBtn"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                <ArchiveButtonicon />
                                <span>{t("pl_archive")}</span>
                              </div>
                            </>
                          }
                        />
                      </Popconfirm>
                    )}
                  </>
                )}
                {authUserData()?.permission.includes(
                  "package|delete_route"
                ) && (
                  <Link className="Archiveicon" to="/add-route/archive">
                    <Archiveicon />
                  </Link>
                )}
                {authUserData()?.permission.includes("package|view_route") && (
                  <div className="gridiconswrap">
                    <Dropdown
                      overlay={columnMenu}
                      trigger={["click"]}
                      placement="bottomRight"
                      visible={dropdownVisible}
                      onVisibleChange={(visible) => setDropdownVisible(visible)}
                    >
                      <Button
                        type="primary"
                        shape="circle"
                        icon={<InsertRowLeftOutlined />}
                        onClick={() => setDropdownVisible(!dropdownVisible)}
                      />
                    </Dropdown>
                  </div>
                )}
              </Space>
            </Form>
          </div>
          {authUserData()?.permission.includes("package|view_route") && (
            <>
              <div className="custom-tablewrp route" ref={tableContainerRef}>
                <VirtualizedTable
                  columns={columnsWithSelection}
                  data={employeeList}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                  rowKey={(record) => record.id}
                  height={tableHeight}
                  width={containerWidth}
                  rowHeight={rowHeight}
                  headerHeight={headerHeight}
                  customCellRender={customCellRender}
                  loading={loading}
                  calculateTotalPrice={calculateTotalPrice}
                  sortState={sortState}
                  setSortState={setSortState}
                  gridRef={gridRef}
                  setScrollPosition={setScrollPosition}
                  scrollPosition={scrollPosition}
                />
              </div>
              <Pagination
                current={state.page}
                pageSize={state.pagesize}
                total={totalCount}
                showSizeChanger
                pageSizeOptions={[
                  "10",
                  "50",
                  "100",
                  "200",
                  "300",
                  "400",
                  "500",
                  "1000",
                ]}
                showTotal={(total) => `Total ${total} items`}
                locale={{ items_per_page: "Items / Page" }}
                onChange={(page, pageSize) => {
                  setState((prev) => ({
                    ...prev,
                    page,
                    pagesize: pageSize,
                  }));
                }}
                style={{ marginTop: 0, textAlign: "right", padding: "8px 0" }}
              />
            </>
          )}
          {authUserData()?.permission.includes("package|add_route") && (
            <div className="btnwrp" style={{ textAlign: "right" }}>
              <Button
                type="primary"
                htmlType="submit"
                className="SaveGradiantBtn"
                onClick={() => handleSubmit()}
                loading={loadingButton}
                // disabled={!checkedDisable}
              >
                {t("pl_create_optimize_route")}
              </Button>
            </div>
          )}
        </div>
      </section>
    </>
  );
};
export default AddRoutePackage;
